import React, { useState } from 'react';
import styled from 'styled-components';
import theme from '../../../../../UI/theme';
import { HeaderRow, StyledTable } from '../../../../installation/table/styles/TableStyles';
import Button from '../../../../UiComponents/Button';
import Checkbox from '../../../../UiComponents/Checkbox';
import InputField from '../../../../UiComponents/InputField';
import Modal from '../../../../UiComponents/Modal';
import { TextMuted } from '../../../../UiComponents/text/StyledText';
import { ButtonRowContainer } from '../DeviceStyles';
import { isSameSubnet, validateIPaddress } from './networkUtils';
import ValidationError from './ValidationError';
import { interfaceKeys } from './networkConfigTypes';
import { loadingStates } from '../../../../installation/installationTypes';
import serverConnection from '../../../../../scripts/server/ServerConnection';
import LoadingSpinner from '../../../../UiComponents/LoadingSpinner';
import { ModalContainer } from '../../../../panel/ModalStyles';
import { Add } from '@material-ui/icons';
import { updateHub } from '../../../../../scripts/hub';

const AddressCell = styled.td`
    width: 130px;
    position: relative;
`

const StyledValidationErrorNob = styled.span`
    position: absolute;
    top: 50%;
    left: calc(100% - 16px);
    transform: translateY(-50%);
    width: 10px;
    height: 10px;
    display: inline-block;
    vertical-align: middle;
    background-color: ${theme.colors.raspberryRed};
`

const FailMessage = styled.span`
    color: ${theme.colors.raspberryRed};
    font-size: ${theme.fontSize.small};
`

const HeaderContainer = styled.div`
    >h4 {
        margin: 0 10px 0 0;
    }
    display: flex;
    align-items: center;
`

const DesktopOnly = styled.div`
  @media (max-width: ${theme.screenSizes.medium-1}px) {
    display: none;
  }
`;

const MobileOnly = styled.div`
  display: none;
  @media (max-width: ${theme.screenSizes.medium-1}px) {
    display: block;
  }
`;

const FieldHeaderCell = styled.th`
  text-align: left;
  white-space: nowrap;
`;

const InterfaceHeaderCell = styled.th`
  text-align: left;
`;

const NarrowCell = styled.td`
  vertical-align: middle;
`;


const IpConfigTable = ({ hub, mainKey, virtualKey }) => {
    const [editState, setEditState] = useState(false);
    const [networkConfig, setNetworkConfig] = useState({});
    const [validationErrors, setValidationErrors] = useState({});
    const [showModal, setShowModal] = useState(false);
    const [modalContent, setModalContent] = useState();
    const [loadingState, setLoadingState] = useState(loadingStates.NONE);
    const [errorMessage, setErrorMessage] = useState();
    const [newEthMac, setNewEthMac] = useState(hub.macAddressEthernet || '');
    const [newWifiMac, setNewWifiMac] = useState(hub.macAddressWifi || '');

    const interfaces = () => {
        const hasVirt = hasVirutalInterface();
        return hasVirt ? [mainKey, virtualKey] : [mainKey];
    };

    const getMacForInterface = (k) => {
        if (editState) {
            return k === interfaceKeys.ETH0 ? newEthMac : newWifiMac;
        }
        if (k === interfaceKeys.ETH0 && hub.macAddressEthernet) return hub.macAddressEthernet;
        if (k === interfaceKeys.WLAN0 && hub.macAddressWifi) return hub.macAddressWifi;
        return '';
    };

    const setEditStateHandler = () => {
        var newEditState = !editState;
        if (newEditState) {
            setNetworkConfig({ ...hub.networkConfig });
            validateNetworkConfig();
            setLoadingState(loadingStates.NONE);
            setErrorMessage(null);
        }
        setEditState(newEditState);
    }

    const updateNetworkConfig = (interfaceKey, key, value) => {
        if (!hasVirutalInterface()) return;
        var updatedNetworkConfig = { ...networkConfig };
        updatedNetworkConfig[interfaceKey][key] = value;
        validateNetworkConfig(updatedNetworkConfig);
        setNetworkConfig(updatedNetworkConfig);
    }

    const addVirtualInterface = () => {
        var updatedNetworkConfig = { ...networkConfig };
        updatedNetworkConfig[virtualKey] = {
            address: '',
            isStatic: true
        };
        validateNetworkConfig(updatedNetworkConfig);
        setNetworkConfig(updatedNetworkConfig);
    }

    const removeVirtualInterface = () => {
        var updatedNetworkConfig = { ...networkConfig };
        delete updatedNetworkConfig[virtualKey];
        validateNetworkConfig(updatedNetworkConfig);
        setNetworkConfig(updatedNetworkConfig);
    }

    const validateInterface = (updatedNetworkConfig, interfaceKey) => {
        let updatedValidationErrors = {};
        let iface = updatedNetworkConfig[interfaceKey];
        if (iface) {
            if (!validateIPaddress(iface.address)) {
                updatedValidationErrors[interfaceKey] = { message: 'Not a valid ip address', type: 'ERROR' };
            } else {

                if (interfaceKey === mainKey) {
                    var current = hub.networkConfig[mainKey];
                    if (!isSameSubnet(current.address, iface.address)) {
                        updatedValidationErrors[interfaceKey] = { message: 'Changed subnet', type: 'WARNING' };
                    }
                } else if (interfaceKey === virtualKey) {
                    var mainInterface = updatedNetworkConfig[mainKey];
                    if (isSameSubnet(mainInterface.address, iface.address)) {
                        updatedValidationErrors[interfaceKey] = { message: 'Same subnet as ' + mainKey, type: 'WARNING' };
                    }
                }
            }
        }
        return updatedValidationErrors;
    }

    const validateNetworkConfig = (config) => {
        var updatedNetworkConfig = config || networkConfig;
        let updatedValidationErrors = validateInterface(updatedNetworkConfig, mainKey);
        updatedValidationErrors = { ...updatedValidationErrors, ...validateInterface(updatedNetworkConfig, virtualKey) }
        setValidationErrors(updatedValidationErrors);
    }

    const saveClickHandler = () => {
        var hasErrors = Object.keys(validationErrors).filter(key => validationErrors[key].type === 'ERROR').length;
        var hasWarnings = Object.keys(validationErrors).filter(key => validationErrors[key].type === 'WARNING').length;
        var content
        if (hasErrors) {
            content = <>
                <p>You cannot save network config with errors</p>
                <Button primary onClick={() => setShowModal(false)}>OK</Button>
            </>
        } else if (hasWarnings) {
            content = <>
                <div>
                    <p>Are you sure you want to save network config with warnings? This can potentially make the hub unreachable</p>
                    <Button primary margin onClick={() => saveHandler()}>Yes</Button>
                    <Button secondary margin onClick={() => setShowModal(false)}>Cancel</Button>
                </div>
            </>
        }

        if (content) {
            setModalContent(content);
            setShowModal(true);
        } else {
            saveHandler();
        }
    }

    const saveHandler = async () => {
        try {
            setErrorMessage(null);
            setLoadingState(loadingStates.LOADING);
            const updatedConfig = hub.networkConfig;
            updatedConfig[mainKey] = networkConfig[mainKey];
            updatedConfig[virtualKey] = networkConfig[virtualKey];
            const res = await serverConnection.hubEventHandler.sendEvent(
                hub.hubId,
                hub.customerId,
                'UPDATE_NETWORK_CONFIG_DIRECT',
                { networkConfig: updatedConfig },
                20000
            );

            // fire and forget mac address updates
            saveMacAddresses();

            if (res.updated) {
                setLoadingState(loadingStates.SUCCESS);
                setEditState(false);
            } else {
                setErrorMessage('Failed to update network config');
                setLoadingState(loadingStates.FAILED);
            }

        } catch (err) {
            console.log('Error - failed to save network config', err)
            setErrorMessage('Failed to update network config');
            setLoadingState(loadingStates.FAILED);
        } finally {
            setShowModal(false);
        }
    }

    const isValidMacAddress = (mac) => {
        if (!mac || mac.length === 0) return true;
        const regex = /^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})$/;
        return regex.test(mac);
    }

    const saveMacAddresses = () => {
        
        let doUpdate = false;

        const updatableHub = {
            hubId: hub.hubId,
            customerId: hub.customerId
        }

        if (newEthMac !== (hub.macAddressEthernet || '')) {
            doUpdate = true;
            updatableHub.macAddressEthernet = newEthMac || null;
        }
        if (newWifiMac !== (hub.macAddressWifi || '')) {
            doUpdate = true;
            updatableHub.macAddressWifi = newWifiMac || null;
        }

        if (doUpdate) {
            try {
                updateHub(updatableHub);
            } catch (err) {
                //pass - not critical
            }
        }

    }

    const displayInterface = (interfaceKey) => {
        var iface = networkConfig[interfaceKey];
        var validationError = validationErrors[interfaceKey];
        if (editState) {
            return <tr key={interfaceKey}>
                <td>{interfaceKey}</td>
                <AddressCell>
                    {iface.isStatic ? <>
                        <InputField
                            small
                            width={130}
                            value={iface.address} onChange={(e) => updateNetworkConfig(interfaceKey, 'address', e.target.value)} />

                        <br />
                        {validationError ? <>
                            <ValidationError validationError={validationError} />
                        </> : <></>}

                    </> : <>
                        {iface.address}
                    </>}
                </AddressCell>
                <AddressCell>
                    <InputField
                        small
                        width={150}
                        style={{ fontSize: '12px' }}
                        value={interfaceKey === interfaceKeys.ETH0 ? newEthMac : newWifiMac}
                        onChange={(e) => {
                            if (interfaceKey === interfaceKeys.ETH0) {
                                setNewEthMac(e.target.value)
                            } else {
                                setNewWifiMac(e.target.value)
                            }
                        }}
                        placeholder="Not set"
                    />
                    {!isValidMacAddress(interfaceKey === interfaceKeys.ETH0 ? newEthMac : newWifiMac) && <StyledValidationErrorNob /> }
                </AddressCell>
                <td>
                    <Checkbox
                        checked={iface.isStatic}
                        disabled={!canSetStatic(interfaceKey)}
                        onChange={() => updateNetworkConfig(interfaceKey, 'isStatic', !iface.isStatic)}
                        backgroundColor={theme.colors.darkSpace}
                    />
                </td>
                <td>
                    {interfaceKey === virtualKey ? <>
                        <Button secondary small onClick={removeVirtualInterface}>Remove</Button>
                    </> : <></>}
                </td>
            </tr>
        } else {
            var iface = hub.networkConfig[interfaceKey];
            let mac = '';
            if (interfaceKey === interfaceKeys.ETH0 && hub.macAddressEthernet) {
                mac = hub.macAddressEthernet;
            } else if (interfaceKey === interfaceKeys.WLAN0 && hub.macAddressWifi) {
                mac = hub.macAddressWifi;
            }
            return <tr key={interfaceKey}>
                <td>{interfaceKey}</td>
                <AddressCell>{getDisplayAddress(interfaceKey, iface)}</AddressCell>
                <AddressCell style={{ width: '150px' }} >{mac}</AddressCell>
                <td>{iface.isStatic ? 'Yes' : 'No'}</td>
            </tr>
        }
    }

    const hasVirutalInterface = () => {
        if (editState) {
            return !!networkConfig[virtualKey];
        } else {
            return !!hub.networkConfig[virtualKey];
        }
    }

    return <>
        {hub.networkConfig && hub.networkConfig[mainKey] ? <>
            <HeaderContainer>
                <h4>Ip addresses</h4>
                <div>
                    <Button small tertiary lightBorder onClick={() => setEditStateHandler()}>
                        {editState ? 'Cancel' : 'Edit'}
                    </Button>
                </div>
            </HeaderContainer>
            <DesktopOnly>
                <StyledTable borderColor={theme.colors.darkSpace80}>
                    <thead>
                        <HeaderRow backgroundColor={theme.colors.darkSpace80}>
                            <th>Interface</th>
                            <th>IP Address</th>
                            <th>Mac Address</th>
                            <th>Static</th>
                            {editState && <th></th>}
                        </HeaderRow>
                    </thead>
                    <tbody>
                        {displayInterface(mainKey)}
                        {hasVirutalInterface() ? <>
                            {displayInterface(virtualKey)}
                        </> : <></>}
                    </tbody>
                </StyledTable>
            </DesktopOnly>
            <MobileOnlyTable
                interfaces={interfaces}
                editState={editState}
                networkConfig={networkConfig}
                hub={hub}
                validationErrors={validationErrors}
                updateNetworkConfig={updateNetworkConfig}
                virtualKey={virtualKey}
                removeVirtualInterface={removeVirtualInterface}
                getMacForInterface={getMacForInterface}
                isValidMacAddress={isValidMacAddress}
                setNewEthMac={setNewEthMac}
                setNewWifiMac={setNewWifiMac}
            />
            {editState ? <>
                <div>
                    {!networkConfig[virtualKey] ? <>
                        <Button
                            secondary
                            small
                            onClick={addVirtualInterface}
                            disabled={networkConfig[virtualKey]}
                        >
                            Add virtual interface
                        </Button>
                    </> : <></>}


                    <ButtonRowContainer>
                        {loadingState === loadingStates.NONE ? <>
                            <Button small primary onClick={saveClickHandler}>Save</Button>
                            <Button small secondary onClick={() => setEditStateHandler(false)}>Cancel</Button>
                        </> : <></>}

                        {loadingState === loadingStates.FAILED ? <>
                            <div><FailMessage>{errorMessage}</FailMessage></div>
                            <Button small primary onClick={saveClickHandler}>Retry</Button>
                            <Button small secondary onClick={() => setEditStateHandler(false)}>Cancel</Button>
                        </> : <></>}

                        {loadingState === loadingStates.LOADING ? <>
                            <LoadingSpinner />
                        </> : <></>}

                    </ButtonRowContainer>
                </div>
            </> : <>
                <div>
                    {loadingState === loadingStates.SUCCESS ? <>
                        <span>Updated</span>
                    </> : <></>}
                </div>
            </>}
        </> : <></>}

        <Modal show={showModal} close={() => setShowModal(false)}>
            <ModalContainer>
                {modalContent}
            </ModalContainer>
        </Modal>

    </>
}

export default IpConfigTable;

const canSetStatic = (key) => {
    return key === interfaceKeys.ETH0;
}

const getDisplayAddress = (key, iface) => {
    var displayAddress = <span>{iface.address}</span>;
    if (key === interfaceKeys.ETH0 && iface.isStatic) {
        if (iface.staticAddress && iface.address != iface.staticAddress) {
            displayAddress = <span>
                Current: {iface.address}<br />
                Set: {iface.staticAddress} <br />
                <TextMuted >Reboot to apply</TextMuted>
            </span>
        }
    }
    return displayAddress;
}

const MobileOnlyTable = ({
    interfaces,
    editState,
    networkConfig,
    hub,
    validationErrors,
    updateNetworkConfig,
    virtualKey,
    removeVirtualInterface,
    getMacForInterface,
    isValidMacAddress,
    setNewEthMac,
    setNewWifiMac
}) => {
    return (
        <MobileOnly>
            <StyledTable borderColor={theme.colors.darkSpace80}>
                <thead>
                <HeaderRow backgroundColor={theme.colors.darkSpace80}>
                    <FieldHeaderCell>Field</FieldHeaderCell>
                    {interfaces().map((k) => (
                    <InterfaceHeaderCell key={`h-${k}`}>{k}</InterfaceHeaderCell>
                    ))}
                </HeaderRow>
                </thead>
                <tbody>
                {/* Row: IP Address */}
                <tr>
                    <td><strong>IP Address</strong></td>
                    {interfaces().map((k) => {
                    const src = editState ? networkConfig : hub.networkConfig;
                    const iface = src[k];
                    const validationError = validationErrors[k];
                    if (editState) {
                        return (
                        <AddressCell key={`ip-${k}`}>
                            {iface.isStatic ? (
                            <>
                                <InputField
                                small
                                width={130}
                                value={iface.address}
                                onChange={(e) => updateNetworkConfig(k, 'address', e.target.value)}
                                />
                                <br />
                                {validationError ? <ValidationError validationError={validationError} /> : null}
                            </>
                            ) : (
                            iface.address
                            )}
                        </AddressCell>
                        );
                    }
                    // read-only
                    return (
                        <AddressCell key={`ip-${k}`}>
                        {getDisplayAddress(k, iface)}
                        </AddressCell>
                    );
                    })}
                </tr>

                {/* Row: Mac Address */}
                <tr>
                    <td><strong>Mac Address</strong></td>
                    {interfaces().map((k) => {
                    const v = getMacForInterface(k);
                    if (editState) {
                        return (
                        <AddressCell key={`mac-${k}`} style={{ width: '150px' }}>
                            <InputField
                            small
                            width={150}
                            style={{ fontSize: '12px' }}
                            value={v}
                            onChange={(e) => {
                                if (k === interfaceKeys.ETH0) setNewEthMac(e.target.value);
                                else setNewWifiMac(e.target.value);
                            }}
                            placeholder="Not set"
                            />
                            {!isValidMacAddress(v) && <StyledValidationErrorNob />}
                        </AddressCell>
                        );
                    }
                    return (
                        <AddressCell key={`mac-${k}`} style={{ width: '150px' }}>
                        {v}
                        </AddressCell>
                    );
                    })}
                </tr>

                {/* Row: Static */}
                <tr>
                    <td><strong>Static</strong></td>
                    {interfaces().map((k) => {
                    const src = editState ? networkConfig : hub.networkConfig;
                    const iface = src[k];
                    if (editState) {
                        return (
                        <NarrowCell key={`static-${k}`}>
                            <Checkbox
                            checked={iface.isStatic}
                            disabled={!canSetStatic(k)}
                            onChange={() => updateNetworkConfig(k, 'isStatic', !iface.isStatic)}
                            backgroundColor={theme.colors.darkSpace}
                            />
                        </NarrowCell>
                        );
                    }
                    return <NarrowCell key={`static-${k}`}>{iface.isStatic ? 'Yes' : 'No'}</NarrowCell>;
                    })}
                </tr>

                {/* Row: Actions (only in edit mode, only shows for virtual) */}
                {editState ? (
                    <tr>
                    <td></td>
                    {interfaces().map((k) => (
                        <NarrowCell key={`act-${k}`}>
                        {k === virtualKey ? (
                            <Button secondary small onClick={removeVirtualInterface}>Remove</Button>
                        ) : null}
                        </NarrowCell>
                    ))}
                    </tr>
                ) : null}
                </tbody>
            </StyledTable>
            </MobileOnly>

    )
}